import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Plan } from '@deepguide-ai/types';
import { trackUpgradeButtonClicked } from '@/utils/analytics/planRestrictionEvents';

interface UpgradeOptions {
  targetPlan: 'STARTER' | 'PRO' | 'ENTERPRISE';
  source?: string; // Analytics tracking
  feature?: string; // Feature that triggered upgrade
  interval?: 'monthly' | 'annual';
}

/**
 * Hook to handle upgrade checkout flow
 *
 * @example
 * ```tsx
 * const { startUpgrade, isUpgrading } = useUpgradeFlow();
 *
 * <Button
 *   onClick={() => startUpgrade({
 *     targetPlan: 'PRO',
 *     source: 'analytics_page',
 *     feature: 'analytics'
 *   })}
 *   loading={isUpgrading}
 * >
 *   Upgrade to PRO
 * </Button>
 * ```
 */
export function useUpgradeFlow() {
  const [isUpgrading, setIsUpgrading] = useState(false);

  const createCheckoutMutation = useMutation(trpc.subscription.createCheckout.mutationOptions());

  const getErrorMessage = (error: unknown) =>
    error instanceof Error && error.message
      ? error.message
      : 'Failed to start upgrade process. Please try again.';

  const startUpgrade = async (options: UpgradeOptions) => {
    setIsUpgrading(true);

    try {
      // Track upgrade button click
      trackUpgradeButtonClicked({
        feature: options.feature || 'unknown',
        source: options.source || 'unknown',
        targetPlan: options.targetPlan,
      });

      // Create Stripe checkout session
      const { url } = await createCheckoutMutation.mutateAsync({
        plan: options.targetPlan,
        interval: options.interval || 'monthly',
      });

      // Redirect to Stripe
      window.location.href = url;
    } catch (error) {
      console.error('Failed to start upgrade:', error);
      toast.error(getErrorMessage(error));
      setIsUpgrading(false);
    }
  };

  return {
    startUpgrade,
    isUpgrading,
  };
}
